import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { InputField, TextAreaField } from '@/components/UI/Fields/ReactFormFields/CreateFieldComponent';
import { SwitchField } from '@/components/UI/Fields/ReactFormFields/SwitchField';
import { Label } from '@/components/UI/Label/Label';
import { getMutatieErrorMessage, isMutatieSucces } from '@/queries/mutaties/dialogMutatieResponseUtils';
import { useMutaties } from '@/queries/mutaties/useMutaties';
import useClientState from '@/state/useClientState';
import { createWijzigInzetOpdrachtData } from '@/utils/helpers';

import styles from '@/components/UI/Dialog/Dialog.module.css';

export type InzetWijzigenDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
  details?: WerklijnContextMenuDetails;
};

export type InzetWijzigenFormProps = {
  opzending: boolean;
  opengesteld: boolean;
  tractie: boolean;
  toelichting: string;
  bijzonderheidscode: string;
};

export const InzetWijzigenDialog = ({ showDialog, handleOpenChange, details }: InzetWijzigenDialogProps) => {
  const lockedWerklijnVersie = details?.werklijnVersie;
  const werklijnen = useClientState((state) => state.werklijnen);
  const werklijnVersieFromState = details?.werklijnId && werklijnen[details.werklijnId]?.versie;
  const [warningMessage, setWarningMessage] = useState<boolean>(false);

  useEffect(() => {
    const shouldShowWarning = werklijnVersieFromState && lockedWerklijnVersie !== werklijnVersieFromState;
    setWarningMessage(!!shouldShowWarning);
  }, [lockedWerklijnVersie, werklijnVersieFromState]);

  const { handleSubmit, reset, control } = useForm<InzetWijzigenFormProps>({
    mode: 'onSubmit',
  });
  const [errorMessage, setErrorMessage] = useState('');

  const { wijzigenInzet } = useMutaties();

  const onSubmit = async (formData: InzetWijzigenFormProps) => {
    // Bug fix for Manco cannot send WijzigenInzet
    const werklijnGegevens = {
      inzetId: details?.wleId as string,
      werklijnId: details?.werklijnId as string,
      versieId: Number(werklijnVersieFromState),
    };
    const { versieId } = werklijnGegevens;
    const isValidVersion = !isNaN(versieId) && versieId >= 0;
    if (werklijnGegevens.inzetId && werklijnGegevens.werklijnId && isValidVersion) {
      const wijzigenInzetOpdracht = createWijzigInzetOpdrachtData(formData, werklijnGegevens);
      const mutatieResponse = await wijzigenInzet(wijzigenInzetOpdracht);
      if (isMutatieSucces(mutatieResponse)) {
        handleOpenChange(false);
        setErrorMessage('');
      } else {
        const errorMessage = getMutatieErrorMessage(mutatieResponse);
        if (errorMessage) {
          setErrorMessage(errorMessage);
        }
      }
    }
  };

  // reset all values
  const resetDialog = (closeButtonCLick: boolean) => {
    reset();
    if (closeButtonCLick) {
      handleOpenChange(false);
    }
  };

  // reset dialog als dialog gesloten wordt via een outsideClick
  useEffect(() => {
    if (!showDialog) {
      resetDialog(false);
    }
    // only reset when showDialog changes
  }, [showDialog]);

  const inzetPositieString = details?.inzetPositie ? `- ${details.inzetPositie}` : '';
  const teWijzigenInzet = `${details?.treinnummer} ${inzetPositieString}`;

  return (
    <MwdDialog
      title='Inzet'
      className={styles.versterkenDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid={`contextMenu-${details?.treinnummer}-${details?.inzetPositie}`}
      hasCloseInHeader={true}
      // error message has priority over warning message
      showNotification={!!errorMessage || warningMessage}
      notificationType={errorMessage ? 'error' : 'warning'}
      notificationText={errorMessage || 'Let op: de werklijnversie is gewijzigd'}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <Label>Treinnummer</Label>
          <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '14px' }}>{teWijzigenInzet}</div>
          <div
            style={{
              backgroundColor: 'var(--tractie-color-border-subtle)',
              height: '2px',
              width: '100%',
              marginBottom: '14px',
              marginTop: '14px',
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            gap: '24px',
            flexDirection: 'column',
          }}
        >
          <div>
            <SwitchField
              name='opzending'
              data-testid='opzending'
              label='In opzending'
              subLabel='Niet opengesteld voor reizigers'
              control={control}
              defaultValue={details?.opzending}
            />
          </div>
          <div>
            <SwitchField
              name='tractie'
              data-testid='tractie'
              label='Tractie'
              subLabel='Materieel levert tractie binnen inzet'
              control={control}
              defaultValue={details?.tractie}
            />
          </div>
        </div>
        <div
          style={{
            backgroundColor: 'var(--tractie-color-border-subtle)',
            height: '2px',
            width: '100%',
            marginBottom: '14px',
            marginTop: '14px',
          }}
        />
        <div>
          <InputField
            width='55%'
            control={control}
            data-testid='bijzonderheidscode'
            maxLength={6}
            name='bijzonderheidscode'
            label='Notitie bij werklijn'
            defaultValue={details?.bijzonderheidscode ?? ''}
          />
        </div>
        <div>
          <TextAreaField
            rows={5}
            data-testid='toelichting'
            maxLength={255}
            control={control}
            name='toelichting'
            label='Notitie'
            defaultValue={details?.toelichting ?? ''}
          />
        </div>
        <MwdDialog.Footer submitCaption='Opslaan inzet' cancelCallback={() => resetDialog(true)} />
      </form>
    </MwdDialog>
  );
};
